import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { tierById } from '#shared/core/ratings';
import { haptic } from '@/lib/haptics';
import { play } from '@/lib/sound';
import { ConfettiBurst, TONE_HEX, useReducedMotion } from '@/ui';
import { copy } from './copy';
import { cardAriaLabel, cardTier, tierLabel } from './model';
import { PlayerCard, type PlayerCardProps } from './PlayerCard';
import { type RevealStateName, tierEffects, useRevealSequence } from './reveal';

/**
 * One pack opening: the sealed back, a tap to rip, the flip to a silhouette, the tier flash, the
 * count-up and the settled card. Tap anywhere while it plays to skip to the end. Reduced motion
 * (OS or in-app toggle) shows the settled card at once, with the tier as text under it.
 *
 * Draft Night mounts one per player in `draftNightOrder` (lowest OVR first), with or without
 * `key={player.id}` (a new player id resets the show on its own). `onSettled` fires once per card,
 * with motion after the show or a skip and under reduced motion as soon as the card is up, so use
 * it to show the Next button. Advance on the user's tap on Next, not on `onSettled` itself, or a
 * reduced-motion crew would flash through every card. The card is the real PlayerCard, driven
 * through `revealState`.
 */
export interface CardRevealProps extends Omit<PlayerCardProps, 'revealState' | 'onPress' | 'size'> {
  size?: 'l' | 'm';
  /** Rip the pack on mount instead of waiting for a tap. */
  autoStart?: boolean;
  /** Fires once per card when it settles: after the show or a skip, or on mount under reduced motion. */
  onSettled?: () => void;
  /** Hide the "Tap to rip" and "Tap to skip" line. */
  hideHint?: boolean;
}

const CONFETTI_COLORS = [TONE_HEX.lavender, TONE_HEX.sky, TONE_HEX.mint, TONE_HEX.butter, TONE_HEX.peach];

export function CardReveal({ size = 'l', autoStart, onSettled, hideHint, className, ...card }: CardRevealProps) {
  const reduced = useReducedMotion();
  const tier = cardTier(card.rating);
  const fx = tierEffects(tier);
  const [bursts, setBursts] = useState(0);
  const fxRef = useRef(fx);
  fxRef.current = fx;

  const seq = useRevealSequence({
    reduced,
    autoStart,
    onSettled,
    onState: (s: RevealStateName) => {
      if (s === 'tear') {
        haptic('tap');
        play('whoosh');
      } else if (s === 'flash') {
        haptic(fxRef.current.haptic);
        play('clank');
        if (fxRef.current.confetti > 0) setBursts((n) => n + 1);
      } else if (s === 'settled' && fxRef.current.confetti > 0) {
        play('swish');
      }
    },
  });

  // A new card (the next Draft Night pick) starts sealed again.
  const id = card.player.id;
  const first = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new player resets the show
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    seq.reset();
  }, [id]);

  const onTap = () => {
    if (seq.state === 'sealed') seq.begin();
    else if (seq.state !== 'settled') seq.skip();
  };
  const hint = seq.state === 'sealed' ? copy.reveal.tapToRip : seq.state === 'settled' ? null : copy.reveal.tapToSkip;

  return (
    <div
      className={clsx('relative flex flex-col items-center gap-3', className)}
      data-testid="card-reveal"
      data-state={seq.state}
    >
      <ConfettiBurst
        trigger={bursts || null}
        count={fx.confetti || 1}
        colors={
          tier === 'myth' ? CONFETTI_COLORS : [tier ? tierById(tier).fill : TONE_HEX.lavender, ...CONFETTI_COLORS]
        }
      />
      <PlayerCard {...card} size={size} revealState={seq.state} onPress={seq.state === 'settled' ? undefined : onTap} />
      {reduced && (
        <p className="tts-display text-lg" data-testid="reveal-tier-text">
          {tierLabel(tier)}
        </p>
      )}
      {!hideHint && hint && !reduced && (
        <p className="font-display text-sm font-black uppercase italic tracking-wide" aria-hidden="true">
          {hint}
        </p>
      )}
      <span role="status" className="sr-only">
        {seq.state === 'settled' ? cardAriaLabel(card.player, card.rating) : ''}
      </span>
    </div>
  );
}
